"use client";

import React, { forwardRef, useMemo, useCallback } from "react";
import { VirtuosoGrid, VirtuosoGridProps, ContextProp } from "react-virtuoso";
import { SwiperItem } from "@/components/SwiperItem/MovieItemComponent";
import { toMovieItem } from "@/lib/movieMapper";
import { slugify } from "@/lib/slugify";
import { GenreContentItem } from "@/types/tag";
import { Movie } from "@/types/movies";
import { SliderSkeleton } from "../pages/home/loading";

interface Props {
  data: GenreContentItem[];
  hasMore?: boolean;
  isLoadingMore?: boolean;
  onLoadMore?: () => void;
}

const gridComponents: NonNullable<VirtuosoGridProps<Movie, undefined>["components"]> = {
  List: forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
    function ListContainer({ style, children, ...props }, ref) {
      return (
        <div ref={ref} {...props} className="grid-container" style={style}>
          {children}
        </div>
      );
    }
  ),
  Item: forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
    function GridItem({ children, ...props }, ref) {
      return (
        <div ref={ref} {...props} style={{ padding: "0.25rem", boxSizing: "border-box" }}>
          {children}
        </div>
      );
    }
  ),
};

export default function VirtuosoMainCardTags({ data, hasMore = false, isLoadingMore = false, onLoadMore }: Props) {
  const items = useMemo<Movie[]>(() => data.map((item) => {
    console.log('item', item)
    const contentType = item.content_type || item.type || "movie";
    return {
      uuid: item.uuid,
      unique_code: item.unique_code,
      entity_type: contentType,
      content_type: contentType,
      title_fa: item.title_fa,
      title_en: item.title_en,
      poster_url: item.poster_url || "/images/poster-placeholder.jpg",
      href: (() => { const s = slugify(item.title_en || item.title_fa); const suf = s ? `/${s}` : ""; return contentType === "series" ? `/s/${item.unique_code}${suf}` : `/movie/${item.unique_code}${suf}`; })(),
      type: contentType,
      release_date: item.release_date,
      duration: item.duration || undefined,
      summary: item.summary,
      imdb_rate: item.imdb_rate ?? 0,
      tags: item.tags ?? [],
      persons: item.persons ?? [],
      subtitles: item.subtitles ?? [],
      trailers: item.trailers ?? [],
      medias: item.medias ?? [],
      categories: item.categories ?? [],
      age_restriction: item.age_restriction,
      has_subtitles:
        Boolean(item.has_subtitles) || (item.subtitles?.length ?? 0) > 0 ? "true" : null,
      has_dubbing:
        Boolean(item.has_dubbing) || (item.dubs?.length ?? 0) > 0 ? "true" : null,
      content_access: item.content_access ?? null,
    };
  }), [data]);

  const footerComponent = useMemo<React.ComponentType<ContextProp<undefined>> | undefined>(() => {
    if (!isLoadingMore) return undefined;
    return function LoadingFooter() {
      return <SliderSkeleton />;
    };
  }, [isLoadingMore]);

  const components = useMemo<NonNullable<VirtuosoGridProps<Movie, undefined>["components"]>>(
    () => footerComponent ? { ...gridComponents, Footer: footerComponent } : gridComponents,
    [footerComponent]
  );

  const handleEndReached = useCallback(() => {
    if (hasMore && !isLoadingMore && onLoadMore) onLoadMore();
  }, [hasMore, isLoadingMore, onLoadMore]);

  if (!items.length) return null;
  console.log("items", items);
  return (
    <VirtuosoGrid
      style={{ width: "100%", height: "auto" }}
      totalCount={items.length}
      useWindowScroll
      components={components}
      itemContent={(index) => <SwiperItem {...toMovieItem(items[index])} />}
      overscan={300}
      endReached={handleEndReached}
    />
  );
}
